// src/voice/DeepgramVoice.js
// Stream mic -> Deepgram WS STT (first-final) -> sendToAgent(text) -> TTS (local if available, else Deepgram MP3) -> play
// Browser-only. No uploads.

const DEEPGRAM_API_KEY = "f98e8934b60d46bd3f3116dd286b455fd8136bb3";

// --------- latency & behavior knobs ---------
const USE_BROWSER_TTS_FIRST = true;      // prefer instant on-device TTS if available
const AUDIO_PLAYBACK_RATE   = 1.05;      // 1.0 = normal; 1.05 is a subtle speed-up
const DG_TTS_MODEL          = "aura-2-thalia-en"; // your current voice/model
const STREAM_TIMESLICE_MS   = 250;       // how often MediaRecorder emits chunks during streaming

// ---------- audio player ----------
export async function playAudioBlob(blob, { rate = AUDIO_PLAYBACK_RATE } = {}) {
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  try { audio.playbackRate = rate; } catch {}
  try { await audio.play(); } catch {}
  audio.addEventListener("ended", () => URL.revokeObjectURL(url), { once: true });
}

/**
 * One-shot mic capture with lightweight VAD + pause/resume;
 * Now accepts onChunk + timeslice to support *streaming* STT while recording.
 */
export async function captureOnce({
  maxMs = 15000,
  silenceMs = 900,
  silenceThreshold = 0.02,
  startArmDelayMs = 300,
  speechKickMs = 120,
  maxWaitForSpeechMs = 5000,
  timesliceMs = STREAM_TIMESLICE_MS,       // <— NEW
  onChunk,                                  // <— NEW: async (Blob) => void
} = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

  // Choose a supported mime type
  let mime = "";
  if (window.MediaRecorder?.isTypeSupported?.("audio/webm;codecs=opus")) {
    mime = "audio/webm;codecs=opus";
  } else if (window.MediaRecorder?.isTypeSupported?.("audio/webm")) {
    mime = "audio/webm";
  }

  const mediaRecorder = mime
    ? new MediaRecorder(stream, { mimeType: mime })
    : new MediaRecorder(stream);

  const chunks = [];

  const AC = window.AudioContext || window.webkitAudioContext;
  const audioCtx = new AC();
  const source = audioCtx.createMediaStreamSource(stream);
  const analyser = audioCtx.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);
  const data = new Uint8Array(analyser.fftSize);

  const t0 = performance.now();
  let heardSpeech = false;
  let loudSince = 0;
  let lastLoudAt = performance.now();
  let stopped = false;
  let isPaused = false;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    try { mediaRecorder.stop(); } catch {}
    try { stream.getTracks().forEach(t => t.stop()); } catch {}
    try { audioCtx.close(); } catch {}
  };

  const pause = async () => {
    if (stopped || isPaused) return;
    try { mediaRecorder.pause(); } catch {}
    try { await audioCtx.suspend(); } catch {}
    isPaused = true;
  };

  const resume = async () => {
    if (stopped || !isPaused) return;
    try { mediaRecorder.resume(); } catch {}
    try { await audioCtx.resume(); } catch {}
    isPaused = false;
    lastLoudAt = performance.now();
    loudSince = 0;
  };

  const result = new Promise((resolve) => {
    mediaRecorder.ondataavailable = async (e) => {
      if (e.data && e.data.size) {
        chunks.push(e.data);
        // stream out while we record
        try { await onChunk?.(e.data); } catch {}
      }
    };
    mediaRecorder.onstop = () => {
      const type = mime || "audio/webm";
      resolve({ blob: new Blob(chunks, { type }), didSpeak: heardSpeech });
    };
  });

  const raf = () => {
    if (stopped) return;

    if (isPaused) {
      requestAnimationFrame(raf);
      return;
    }

    analyser.getByteTimeDomainData(data);
    const peak = Math.max(...data.map(v => Math.abs(v - 128))) / 128; // 0..1
    const now = performance.now();

    if (now - t0 >= startArmDelayMs) {
      if (peak >= silenceThreshold) {
        if (!heardSpeech) {
          if (loudSince === 0) loudSince = now;
          if (now - loudSince >= speechKickMs) {
            heardSpeech = true;
          }
        }
        lastLoudAt = now;
      } else {
        loudSince = 0;
      }

      if (heardSpeech && now - lastLoudAt >= silenceMs) {
        stop();
        return;
      }

      if (!heardSpeech && now - t0 >= maxWaitForSpeechMs) {
        stop();
        return;
      }
    }

    if (now - t0 >= maxMs) {
      stop();
      return;
    }

    requestAnimationFrame(raf);
  };

  // Start streaming chunks periodically
  mediaRecorder.start(timesliceMs);
  requestAnimationFrame(raf);
  setTimeout(stop, maxMs + 1000);

  return { stop, pause, resume, result };
}

// ---------- Deepgram STT (fallback: single-shot REST) ----------
export async function transcribeWebmBlob(blob) {
  const url = "https://api.deepgram.com/v1/listen?model=nova-2";
  const res = await fetch(url, {
    method: "POST",
    mode: "cors",
    cache: "no-store",
    headers: {
      Authorization: `Token ${DEEPGRAM_API_KEY}`,
      "Content-Type": "audio/webm",
    },
    body: blob,
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    console.warn("Deepgram STT failed:", res.status, errText);
    return "";
  }
  const json = await res.json();
  const t =
    json?.results?.channels?.[0]?.alternatives?.[0]?.transcript ??
    json?.transcript ??
    "";
  return (t || "").trim();
}

/* ---------- Streaming STT (fast path) ---------- */

function openDeepgramSocket() {
  // Browser auth via subprotocol: ['token', <API KEY>]
  // Send Opus chunks straight from MediaRecorder.
  const qs = [
    "model=nova-2",
    "encoding=opus",
    "sample_rate=48000",
    // keep defaults light to minimize post-processing latency
    // (punctuation/smart_formatting add a bit of latency; enable if you need them)
    "punctuate=false",
    "smart_format=false",
    "interim_results=false"
  ].join("&");

  const url = `wss://api.deepgram.com/v1/listen?${qs}`;
  const ws = new WebSocket(url, ["token", DEEPGRAM_API_KEY]);

  return ws;
}

function extractTranscript(evtData) {
  let obj;
  try { obj = JSON.parse(evtData); } catch { return { text: "", isFinal: false }; }

  const channel = obj?.channel || obj?.results?.channels?.[0];
  const alt = channel?.alternatives?.[0] || obj?.alternatives?.[0];

  const text = (alt?.transcript || obj?.transcript || "").trim();

  // ✅ add parentheses to avoid no-mixed-operators
  const isFinal = Boolean(
    alt?.is_final ||
    obj?.is_final ||
    obj?.speech_final ||
    obj?.type?.toLowerCase?.().includes?.("final") ||
    (obj?.type === "Results" && obj?.final)
  );

  return { text, isFinal };
}

/**
 * Streaming mic → Deepgram WS.
 * Returns the first *final* transcript ASAP.
 * If socket fails, resolves to "" (caller should fall back to REST).
 */
async function streamFirstFinal({ onChunkFromMic }) {
  return new Promise((resolve) => {
    let resolved = false;
    const ws = openDeepgramSocket();
    const pending = []; // queue chunks until ws opens

    const safeResolve = (text) => {
      if (resolved) return;
      resolved = true;
      try { ws.close(1000); } catch {}
      resolve(text || "");
    };

    ws.onopen = () => {
      // flush any queued chunks
      if (pending.length) {
        for (const u8 of pending) {
          try { ws.send(u8); } catch {}
        }
        pending.length = 0;
      }
    };

    ws.onerror = () => safeResolve("");
    ws.onclose = () => safeResolve(""); // if closed before final, fallback

    ws.onmessage = (evt) => {
      const { text, isFinal } = extractTranscript(evt.data);
      if (isFinal && text) {
        safeResolve(text);
      }
    };

    // attach streaming of mic chunks
    onChunkFromMic(async (blob) => {
      try {
        const buf = await blob.arrayBuffer();
        const u8 = new Uint8Array(buf);
        if (ws.readyState === WebSocket.OPEN) ws.send(u8);
        else pending.push(u8);
      } catch {}
    });
  });
}

/* ---------- TTS (local first; Deepgram MP3 fallback) ---------- */

function hasBrowserTTS() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

async function speakLocal(text, { rate = AUDIO_PLAYBACK_RATE } = {}) {
  return new Promise((resolve) => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = rate;
      u.onend = resolve;
      u.onerror = resolve;
      window.speechSynthesis.speak(u);
    } catch {
      resolve();
    }
  });
}

// Kept name for compatibility. Generates MP3 for speed (smaller than WAV).
export async function synthesizeToWav(
  text,
  { model = DG_TTS_MODEL, encoding = "mp3", container = "mp3" } = {}
) {
  const url =
    `https://api.deepgram.com/v1/speak?model=${encodeURIComponent(model)}` +
    `&encoding=${encodeURIComponent(encoding)}&container=${encodeURIComponent(container)}`;

  const res = await fetch(url, {
    method: "POST",
    mode: "cors",
    cache: "no-store",
    headers: {
      Authorization: `Token ${DEEPGRAM_API_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    console.warn("Deepgram TTS failed:", res.status, errText);
    return null;
  }
  const buf = await res.arrayBuffer();
  return new Blob([buf], { type: "audio/mpeg" });
}

async function speakDeepgram(text) {
  const audio = await synthesizeToWav(text); // now MP3
  if (audio) await playAudioBlob(audio);
}

/**
 * Orchestrates one-shot voice turn; returns { transcript, agentText, didSpeak }.
 * Streams STT for first-final, falls back to REST if needed.
 * Accepts onController to expose pause/resume/stop to UI while recording.
 */
export async function voiceTurn({ sendToAgent, captureOptions, onController } = {}) {
  // allow streaming to hook mic chunks
  let streamOnChunk = null;
  const { stop, pause, resume, result } = await captureOnce({
    ...captureOptions,
    onChunk: async (blob) => { try { await streamOnChunk?.(blob); } catch {} },
  });
  onController?.({ pause, resume, stop });

  // Start streaming STT in parallel with recording:
  const firstFinalPromise = streamFirstFinal({
    onChunkFromMic: (fn) => { streamOnChunk = fn; },
  });

  // Wait for recording to finish (VAD) or first-final to arrive — whichever is sooner
  let transcript = "";
  try {
    transcript = await Promise.race([
      firstFinalPromise.then((t) => {
        if (t) {
          // We have a final transcript — cut the mic right away.
          try { stop(); } catch {}
        }
        return t;
      }),
      (async () => {
        const { blob: recorded, didSpeak } = await result;
        if (!didSpeak) return "";
        // If streaming didn’t produce a transcript, do a REST fallback.
        const t = await transcribeWebmBlob(recorded);
        return t;
      })(),
    ]);
  } catch {
    // worst case, try REST after stop
    try {
      const { blob: recorded } = await result;
      transcript = await transcribeWebmBlob(recorded);
    } catch {
      transcript = "";
    }
  } finally {
    onController?.(null); // hide UI controls
  }

  if (!transcript) {
    return { transcript: "", agentText: null, didSpeak: false };
  }

  // Send to your agent immediately
  const agentText = await sendToAgent(transcript);

  // Speak reply — local first, Deepgram fallback
  try {
    if (USE_BROWSER_TTS_FIRST && hasBrowserTTS()) {
      await speakLocal(agentText);
    } else {
      await speakDeepgram(agentText);
    }
  } catch {
    try { await speakDeepgram(agentText); } catch {}
  }

  return { transcript, agentText, didSpeak: true };
}
